import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper, { Area, MediaSize, Point, Size } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import {
  Cross2Icon,
  MagnifyingGlassIcon,
  RotateCounterClockwiseIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { Spinner } from '../Spinner';
import { getCroppedImageFile } from '../../utils/cropImage';
import './PhotoCropModal.scss';

export interface PhotoCropModalProps {
  isOpen: boolean;
  imageSrc: string | null;
  fileName?: string;
  mimeType?: string;
  onClose: () => void;
  onApply: (file: File) => void | Promise<void>;
  isApplying?: boolean;
}

export const PhotoCropModal: React.FC<PhotoCropModalProps> = ({
  isOpen,
  imageSrc,
  fileName = 'profile-photo.jpg',
  mimeType = 'image/jpeg',
  onClose,
  onApply,
  isApplying = false,
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const pendingMediaSizeRef = useRef<MediaSize | null>(null);
  const cropSizeRef = useRef<Size | null>(null);

  useEffect(() => {
    if (!imageSrc) return;

    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    pendingMediaSizeRef.current = null;
    cropSizeRef.current = null;
  }, [imageSrc]);

  const adjustPortraitInitialZoom = useCallback(() => {
    const mediaSize = pendingMediaSizeRef.current;
    const cropSize = cropSizeRef.current;
    if (!mediaSize || !cropSize) return;

    const isPortrait = mediaSize.naturalHeight > mediaSize.naturalWidth;
    if (!isPortrait) {
      setMinZoom(1);
      return;
    }

    const initialZoom = cropSize.width / mediaSize.width;
    const nextMinZoom = Math.min(initialZoom, 1);
    setMinZoom(nextMinZoom);
    setZoom(Math.min(Math.max(initialZoom, nextMinZoom), 3));
    setCrop({ x: 0, y: 0 });
  }, []);

  const handleMediaLoaded = useCallback(
    (mediaSize: MediaSize) => {
      pendingMediaSizeRef.current = mediaSize;
      adjustPortraitInitialZoom();
    },
    [adjustPortraitInitialZoom]
  );

  const handleCropSizeChange = useCallback(
    (cropSize: Size) => {
      cropSizeRef.current = cropSize;
      adjustPortraitInitialZoom();
    },
    [adjustPortraitInitialZoom]
  );

  const onCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleRotateCounterClockwise = useCallback(() => {
    setRotation((prev) => prev - 90);
  }, []);

  const handleRotateClockwise = useCallback(() => {
    setRotation((prev) => prev + 90);
  }, []);

  const handleApply = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const croppedFile = await getCroppedImageFile(
        imageSrc,
        croppedAreaPixels,
        rotation,
        fileName,
        mimeType
      );
      await onApply(croppedFile);
    } catch (err) {
      console.error('Failed to crop image:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [
    imageSrc,
    croppedAreaPixels,
    rotation,
    fileName,
    mimeType,
    onApply,
  ]);

  const handleClose = useCallback(() => {
    if (isApplying || isProcessing) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    onClose();
  }, [isApplying, isProcessing, onClose]);

  if (!isOpen || !imageSrc) return null;

  const zoomPercent = Math.round(zoom * 100);
  const zoomSliderProgress =
    minZoom === 3 ? 100 : ((zoom - minZoom) / (3 - minZoom)) * 100;
  const busy = isApplying || isProcessing;

  return (
    <div className="photo-crop-modal" role="presentation">
      <div
        className="photo-crop-modal__overlay"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        className="photo-crop-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="photo-crop-modal-title"
      >
        <header className="photo-crop-modal__header">
          <div>
            <h2 id="photo-crop-modal-title" className="photo-crop-modal__title">
              Crop Your Photo
            </h2>
            <p className="photo-crop-modal__subtitle">
              Adjust your profile picture to fit the square frame perfectly. This
              is how other members will see you on the platform.
            </p>
          </div>
          <button
            type="button"
            className="photo-crop-modal__close"
            onClick={handleClose}
            disabled={busy}
            aria-label="Close crop dialog"
          >
            <Cross2Icon />
          </button>
        </header>

        <div className="photo-crop-modal__crop-area">
          <Cropper
            key={imageSrc}
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            rotation={rotation}
            aspect={1}
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onMediaLoaded={handleMediaLoaded}
            onCropSizeChange={handleCropSizeChange}
            objectFit="cover"
          />
        </div>

        <div className="photo-crop-modal__controls">
          <div className="photo-crop-modal__control-row">
            <div className="photo-crop-modal__control-label">
              <MagnifyingGlassIcon aria-hidden="true" />
              <span>Zoom</span>
            </div>
            <input
              type="range"
              min={minZoom}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="photo-crop-modal__slider"
              style={{
                ['--slider-progress' as string]: `${zoomSliderProgress}%`,
              }}
              aria-label="Zoom"
            />
            <span className="photo-crop-modal__zoom-value">{zoomPercent}%</span>
          </div>

          <div className="photo-crop-modal__control-row photo-crop-modal__control-row--rotate">
            <div className="photo-crop-modal__control-label">
              <ReloadIcon aria-hidden="true" />
              <span>Rotate</span>
            </div>
            <div className="photo-crop-modal__rotate-actions">
              <button
                type="button"
                className="photo-crop-modal__rotate-btn"
                onClick={handleRotateCounterClockwise}
                aria-label="Rotate counter-clockwise"
              >
                <RotateCounterClockwiseIcon />
              </button>
              <button
                type="button"
                className="photo-crop-modal__rotate-btn"
                onClick={handleRotateClockwise}
                aria-label="Rotate clockwise"
              >
                <ReloadIcon />
              </button>
            </div>
          </div>
        </div>

        <footer className="photo-crop-modal__footer">
          <button
            type="button"
            className="photo-crop-modal__cancel"
            onClick={handleClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="photo-crop-modal__apply"
            onClick={handleApply}
            disabled={busy || !croppedAreaPixels}
          >
            {busy ? <Spinner size="small" /> : null}
            {busy ? 'Applying...' : 'Apply Crop'}
          </button>
        </footer>
      </div>
    </div>
  );
};
